import React, { useEffect, useState } from 'react';
import { safeFetch } from '../../lib/api';
import { ADMIN_JOBS_TABS, countPendingInvitations, countPendingJobs } from '../../lib/adminJobsTabs';

/**
 * AdminJobsTabBar — the Jobs page tab bar (admin sidebar redesign,
 * Oct 2026): Job List · Offers · Invitations over the three existing
 * admin views. Tab ids are the existing adminTab ids, and selection goes
 * through the same nav:change plumbing as every other admin navigation,
 * so deep links (/jobs, /offers, /invitations) and the browser path stay
 * in sync exactly as before.
 *
 * Pending-action bubbles (owner addition): ONE aggregated count load on
 * mount (the bar remounts when the jobs-area tab changes) reuses the
 * same payloads the three pages load for themselves. A source that fails
 * leaves its count unknown and shows NO bubble — never a wrong number —
 * and never blocks the page. Bubbles render only when the count is > 0,
 * as a solid attention pill (the portal's badge red), distinct from the
 * muted grey pills used for plain totals.
 */
export default function AdminJobsTabBar({
  activeTab,
  onSelect,
}: {
  activeTab: string;
  onSelect: (tabId: string) => void;
}) {
  const [counts, setCounts] = useState<Record<string, number | null>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [jobsRes, offersRes, invitationsRes] = await Promise.allSettled([
        safeFetch('/api/admin/jobs'),
        // 'issued' is the Offers console's sent / awaiting-decision state;
        // placement-approval is a separate bucket and is NOT folded in.
        safeFetch('/api/admin/offers?status=issued&page=1&pageSize=1'),
        safeFetch('/api/admin/invitations'),
      ]);
      if (cancelled) return;
      setCounts({
        jobs:
          jobsRes.status === 'fulfilled'
            ? countPendingJobs((jobsRes.value as any)?.jobs)
            : null,
        offers:
          offersRes.status === 'fulfilled' &&
          typeof (offersRes.value as any)?.total === 'number'
            ? (offersRes.value as any).total
            : null,
        invitations:
          invitationsRes.status === 'fulfilled'
            ? countPendingInvitations((invitationsRes.value as any)?.invitations)
            : null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  return (
    <div className="bg-white border-b border-slate-200 px-4 sm:px-6 shrink-0">
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto" role="tablist" aria-label="Jobs sections">
        {ADMIN_JOBS_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = counts[tab.id];
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(tab.id)}
              className={`relative flex items-center gap-2 px-3 py-3 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 ${
                isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {tab.label}
              {typeof count === 'number' && count > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold leading-none shadow-sm">
                  {count > 99 ? '99+' : count}
                </span>
              )}
              {isActive && <span className="absolute inset-x-2 bottom-0 h-[3px] rounded-full bg-blue-600" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
